import React, { useState } from 'react'
import {
  Card,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Button,
  Collapse,
  InputAdornment,
  Divider,
} from '@mui/material'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Editor from 'react-simple-wysiwyg'
import styled from 'styled-components'

const StyledEditorWrapper = styled.div`
  > .rsw-editor {
    resize: vertical;
    background-color: #fff;
    font-size: 14px;
    line-height: 1.5;
    color: #495057;
    min-height: 120px;
    max-height: 300px;
    overflow-y: auto;
    padding: 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
  }
  > .rsw-editor .rsw-ce {
    outline: none;
  }

  > .rsw-editor:focus-within {
    outline: 2px solid #1976d2;
  }

  > .rsw-toolbar {
    border: 1px solid #ced4da;
    border-bottom: none;
    border-radius: 4px 4px 0 0;
    background-color: #f1f3f5;
  }

  & ul {
    list-style: disc;
    padding-left: 2em;
  }

  & ol {
    list-style: decimal;
    padding-left: 2em;
  }
`

const initialTasks = [
  'Complete FEGLI Change Form',
  'Choose Retirement Date',
  'Schedule Appointment to Fill out Retirement Paperwork',
  'Review TSP Fund Allocations',
  'Build Financial Framework for Retirement',
  'Military Time Buyback',
  'Submit FEGLI Reimbursement Letter for Option C',
  'Gather Additional Info on Financial Particulars',
  'Apply for Social Security',
  'Repay TSP Loan',
  'Take a TSP Loan',
  'Change FEHB to Self + 1 Coverage',
  'Change FEHB to Family Coverage',
  'Change FEHB to Self Coverage',
  'Check on FEHB Rates During Open Season in November',
  'Change TSP Roth Contributions to Traditional TSP',
  'Contribute at Least 5% to TSP',
  'Contribute More to TSP (Already Contributing at Least 5%)',
  'Open a Roth IRA',
  'Get Social Security Estimate from SSA.gov',
  'Contemplate Retirement Goals',
  'Take out TSP Loan to Pay off Credit Card Debt',
  'Investigate G Fund Alternatives',
  'Investigate Refinancing Options',
  'Investigate Federal Student Loan Forgiveness Program',
  'Investigate Private Life Insurance Options',
  'Pursue FEGLI Option C Reimbursement',
  'Pick up Medicare Part A for free',
  'Consolidate Parent Plus Loans Into Direct Consolidated Loans',
  'Pursue Public Student Loan Forgiveness',
  'Pursue FEHB Reimbursement',
  'Preserve Annual Leave in Final Working Year for Max Sellback at Retirement',
  'Prepare for Financially Uncertain Interim Period',
  'Enroll in Medicare Part B within 8 Months of Separation Date',
  'Enroll in Medicare Part B within +/- 3 MOs of 65th Birthday',
  'Create Blue Cross MRA Account for $800/YR Reimbursements',
  'Drain FSA Before Separation Date',
  'Implement Sustainable Withdrawal Strategy to Supplement Retirement Income',
]

// Convert the list to an object with all checkboxes initially unchecked
// const defaultTasksState = Object.fromEntries(
//   initialTasks.map((task) => [task, false])
// )

export const StickyNotes = ({
  intakeState,
  updateIntakeStateAndSubmit,
  updateIntakeStateAtKey,
}) => {
  const [open, setOpen] = useState(false)
  const [openNotes, setOpenNotes] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [html, setHtml] = useState(intakeState?.userNotes)

  function editorOnChange(e) {
    setHtml(e.target.value)
  }

  const handleBlur = (e) => {
    e.target.style.height = '200px'
    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({ userNotes: html })
    )()
  }

  const userTasks = intakeState?.userTasks ? intakeState.userTasks : []
  const allTasks = Array.from(new Set([...initialTasks, ...userTasks]))

  // Sort tasks to bring checked tasks to the top
  const sortedTasks = allTasks.sort(
    (a, b) => userTasks.includes(b) - userTasks.includes(a)
  )

  // Filter tasks based on search term
  const filteredTasks = sortedTasks.filter((task) =>
    task.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const toggleTask = (task) => {
    const updatedTasks = userTasks.includes(task)
      ? userTasks.filter((t) => t !== task)
      : [...userTasks, task]
    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({ userTasks: updatedTasks })
    )()
  }

  const addTask = () => {
    if (searchTerm.trim() && !allTasks.includes(searchTerm.trim())) {
      const updatedTasks = [...userTasks, searchTerm.trim()]
      updateIntakeStateAndSubmit(() =>
        updateIntakeStateAtKey({ userTasks: updatedTasks })
      )()
      setSearchTerm('')
    }
  }

  return (
    <Card
      sx={{
        position: 'fixed',
        backgroundColor: '#f8f9fa',
        bottom: 8,
        width: 'inherit',
        textAlign: 'left',
        boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
        borderRadius: 2,
        p: 2,
        pt: 0,
        zIndex: 1100,
      }}
    >
      <Button
        variant="outlined"
        fullWidth
        sx={{
          mt: 1,
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          textTransform: 'none',
          fontSize: '15px',
          padding: '10px 4px 9px',
          color: '#344767',
          fontWeight: 'normal',
          boxShadow: 'none',
          border: 'none',
          '&:hover, &:focus, &:active, &:focus:not(:hover)': {
            transform: 'none',
            boxShadow: 'none',
            border: 'none',
          },
        }}
        endIcon={
          <ExpandMoreIcon
            style={{
              transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s ease',
            }}
          />
        }
        onClick={() => setOpen(!open)}
      >
        {open ? 'Hide Services Required' : 'Services Required'}
      </Button>
      <Divider
        sx={{ marginTop: 0, marginBottom: 0, backgroundColor: '#344767' }}
      />

      <Collapse in={open}>
        <TextField
          id="search-add-input"
          placeholder="Find or Add Tasks"
          fullWidth
          variant="outlined"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault() // Prevent form submission
              addTask()
            }
          }}
          sx={{ marginBottom: '16px' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  variant="contained"
                  sx={{
                    fontSize: '15px',
                    padding: '10px 24px 9px',
                    backgroundColor: '#344767',
                    color: '#fff',
                    fontWeight: 'normal',
                    boxShadow: 'none',
                    borderRadius: '0px 8px 8px 0px',
                    position: 'absolute',
                    top: -1,
                    right: 0,
                    '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                      transform: 'none',
                      backgroundColor: '#293852',
                      boxShadow: 'none',
                    },
                  }}
                  onClick={addTask}
                >
                  Add +
                </Button>
              </InputAdornment>
            ),
          }}
        />
        <FormGroup sx={{ margin: '0px 0px 10px 4px' }}>
          <Grid
            container
            spacing={1}
            sx={{
              overflowY: 'scroll',
              maxHeight: '250px',
              '& label': {
                display: 'inline-flex',
              },
            }}
          >
            {filteredTasks.map((task) => (
              <Grid
                item
                xs={12}
                key={task}
                sx={{ padding: '4px 0 0 16px !important' }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userTasks.includes(task)}
                      onChange={() => toggleTask(task)}
                      name={task}
                    />
                  }
                  label={task}
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </Collapse>
      <Button
        variant="outlined"
        fullWidth
        sx={{
          mt: 1,
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          textTransform: 'none',
          fontSize: '15px',
          padding: '10px 4px 9px',
          color: '#344767',
          fontWeight: 'normal',
          boxShadow: 'none',
          border: 'none',
          '&:hover, &:focus, &:active, &:focus:not(:hover)': {
            transform: 'none',
            boxShadow: 'none',
            border: 'none',
          },
        }}
        endIcon={
          <ExpandMoreIcon
            style={{
              transform: openNotes ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s ease',
            }}
          />
        }
        onClick={() => setOpenNotes(!openNotes)}
      >
        {openNotes ? 'Hide Notes' : 'Client Notes'}
      </Button>
      <Collapse in={openNotes}>
        <StyledEditorWrapper>
          <Editor value={html} onChange={editorOnChange} onBlur={handleBlur} />
        </StyledEditorWrapper>
      </Collapse>
    </Card>
  )
}
